import { Sparkles, Shield, TrendingUp, Users, FileText, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/")}>홈</Button>
              <Button variant="default" className="bg-blue-500 hover:bg-blue-600">소개</Button>
              <Button variant="ghost" onClick={() => navigate("/methodology")}>방법론</Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/consultation')}
              >
                상담신청
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-500 text-white">KAILA Framework</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              제안 배경 및 필요성
            </h1>
            <p className="text-xl text-muted-foreground">
              AI 시대, 조직의 경쟁력을 결정하는 핵심 역량
            </p>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 border-border mb-8">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                  제안 배경
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose prose-lg max-w-none dark:prose-invert">
                  <p className="text-lg leading-relaxed">
                    인공지능(AI)은 4차 산업혁명 핵심 기술로, 시장조사기관 IDC는 <strong className="text-blue-600 dark:text-blue-400">2024~2028년 연평균 27%의 성장률</strong>을 예상하고 있습니다. 
                    귀사를 비롯한 다수의 선도 기업이 AI 시스템을 도입‧확산하고 있으나, 다층적 규제 아래에서는 '기술적 이해'만으로는 한계가 있습니다.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4 my-8">
                  <Card className="bg-blue-500/5 border-blue-500/20">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Shield className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                        <h3 className="font-bold mb-1">AI 기본법</h3>
                        <p className="text-sm text-muted-foreground">2026.1 시행 예정</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-500/5 border-purple-500/20">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Scale className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                        <h3 className="font-bold mb-1">EU AI Act</h3>
                        <p className="text-sm text-muted-foreground">2024.6 발효</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-500/5 border-green-500/20">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <FileText className="h-8 w-8 text-green-500 mx-auto mb-2" />
                        <h3 className="font-bold mb-1">개인정보보호법</h3>
                        <p className="text-sm text-muted-foreground">지속적 강화</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/5 dark:to-purple-500/5 rounded-lg p-6 border border-blue-500/20">
                  <h3 className="font-bold text-xl mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-500" />
                    AI 리터러시(AI Literacy)란?
                  </h3>
                  <p className="text-lg leading-relaxed">
                    기술·윤리·법무·보안 관점을 통합한 <strong className="text-blue-600 dark:text-blue-400">"AI를 안전하고 책임감 있게 설계·검증·활용·관리할 수 있는 조직 역량"</strong>을 의미합니다.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Necessity Section */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Users className="h-6 w-6 text-purple-500" />
                  제안의 필요성
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <Shield className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">규제 준수 지원</h3>
                      <p className="text-muted-foreground">
                        강화되는 AI 관련 법규 및 규제에 대한 조직의 이해도와 준수 현황을 점검하고, 필요한 개선 방안을 제시합니다.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                      <Users className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">책임감 있는 AI 문화 조성</h3>
                      <p className="text-muted-foreground">
                        AI 기술의 윤리적이고 안전한 활용에 대한 인식을 제고하고, 조직 내 책임감 있는 AI 문화를 조성합니다.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">AI 리터러시 수준 측정</h3>
                      <p className="text-muted-foreground">
                        조직 구성원의 AI에 대한 기본적인 이해, 활용 능력, 윤리적 인식 수준을 종합적으로 파악합니다.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">AI 활용 역량 강화</h3>
                      <p className="text-muted-foreground">
                        진단 결과를 바탕으로 맞춤형 교육 및 개선 방안을 제시하여 조직 전체의 AI 활용 역량을 강화합니다.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                      <Shield className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">잠재적 위험 식별과 예방</h3>
                      <p className="text-muted-foreground">
                        AI 활용 과정에서 발생할 수 있는 기술·보안·윤리적 위험 요소의 사전 감지 및 대응체계 구축
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6">
                  <p className="text-lg font-medium text-center">
                    본 제안서는 귀사의 AI 리터러시 수준을 각 분야의 전문가들이 체계적으로 진단하고, 평가하며 추가적인 로드맵(교육·가이드라인·프로세스)으로 이어지는 종합적이고 입체적인 <strong>"컴플라이언스 기반 AI 역량 강화"</strong>를 목적으로 합니다.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="mt-12 text-center">
              <Button 
                size="lg" 
                className="bg-blue-500 hover:bg-blue-600"
                onClick={() => navigate("/consultation")}
              >
                무료 상담 신청하기
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold">KAILA</span>
                    <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  기업의 AI 리터러시 수준을 과학적으로 진단하고<br />
                  체계적으로 개선하는 전문 프레임워크
                </p>
              </div>

              <div>
                <h3 className="font-bold mb-4">프레임워크</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    홈
                  </button>
                  <button onClick={() => navigate('/about')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    소개
                  </button>
                  <button onClick={() => navigate('/methodology')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    방법론
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-4">문의</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/consultation')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    상담 신청
                  </button>
                  <button onClick={() => navigate('/resources')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    자료 요청
                  </button>
                  <button onClick={() => navigate('/partnership')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    파트너십
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  © 2025 KAILA Framework. All rights reserved.
                </p>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <button onClick={() => navigate('/admin/login')} className="hover:text-foreground transition-colors">
                    관리자 로그인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
